import { createError } from '@common/neo.tax';
import { useCallback, useEffect, useState } from 'react';

import { DEFAULT_MONGO_DB_YEAR } from '@neo.tax/shared/constants';
import { PeriodName, ProductType } from '@neo.tax/shared/graphql';
import {
  useAllFiscalYears,
  useCompany,
  useCurrentCompanyIdOptional,
  useSelectedFiscalYearIdOptional,
  useSelectedPeriodNameOptional,
  useSelectedProductOptional,
} from '@neo.tax/shared/hooks';
import { useFlags } from '@neo.tax/shared/third-parties';
import {
  getProductAndPeriodFromLocalStorage,
  ProductPeriodSelection,
  storeProductAndPeriodInLocalStorage,
} from '@neo.tax/shared/utils';

export type UseDefaultProductAndPeriod = {
  periodName: PeriodName | null;
  fiscalYearId: string | null;
  product: ProductType | null;
  loading: boolean;
  updateProductPeriodFromLocalStorage: (value: ProductPeriodSelection) => void;
};

/**
 * Default period is the period to be selected when no period is selected.
 * e.g. when the user logs in.
 * The priority is:
 * 1. Product and Period in the URL
 * 2. Product in the URL and period is in local storage -> Use it
 * 3. Product in the URL and period is not in local storage -> Open period modal
 * 4. Product and period is in local storage -> Use it
 * 5. MongoDB: RD 2021 (throws without fiscal year)
 * 6. Latest RD Fiscal Year OR null
 */
export const useDefaultProductAndPeriod = (loadingLaunchDarklyIdentifyCompany: boolean): UseDefaultProductAndPeriod => {
  const companyId = useCurrentCompanyIdOptional();
  const { fiscalYears } = useAllFiscalYears({ skip: !companyId });
  const { company } = useCompany({ companyId });
  const flags = useFlags();
  const periodName = useSelectedPeriodNameOptional();
  const fiscalYearId = useSelectedFiscalYearIdOptional();
  const selectedProduct = useSelectedProductOptional();

  const [productPeriodFromLocalStorage, setProductPeriodFromLocalStorage] = useState<ProductPeriodSelection | null>(
    () => (companyId ? getProductAndPeriodFromLocalStorage(companyId) : null),
  );

  useEffect(() => {
    setProductPeriodFromLocalStorage(companyId ? getProductAndPeriodFromLocalStorage(companyId) : null);
  }, [companyId]);

  const updateProductPeriodFromLocalStorage = useCallback(
    (value: ProductPeriodSelection) => {
      if (!companyId) {
        return;
      }
      storeProductAndPeriodInLocalStorage(companyId, value.fiscalYearId, value.periodName, value.product);
      setProductPeriodFromLocalStorage(value);
    },
    [companyId],
  );

  if (!company || !fiscalYears) {
    return { periodName: null, fiscalYearId: null, product: null, loading: true, updateProductPeriodFromLocalStorage };
  }

  // If the product is in the URL, use it
  if (selectedProduct) {
    // If the product and period are in the URL, use it
    if (fiscalYearId && periodName) {
      return {
        product: selectedProduct,
        periodName,
        fiscalYearId,
        loading: false,
        updateProductPeriodFromLocalStorage,
      };
    }
    // If the product and period are in local storage and the product is the same as the selected product, use it
    if (productPeriodFromLocalStorage?.product === selectedProduct) {
      return {
        product: selectedProduct,
        periodName: productPeriodFromLocalStorage.periodName,
        fiscalYearId: productPeriodFromLocalStorage.fiscalYearId,
        loading: false,
        updateProductPeriodFromLocalStorage,
      };
    }
    // If the product is in the URL but the period is not, open the period modal
    return {
      product: selectedProduct,
      periodName: null,
      fiscalYearId: null,
      loading: false,
      updateProductPeriodFromLocalStorage,
    };
  }

  // If the period is in local storage, use it
  if (productPeriodFromLocalStorage) {
    return {
      product: productPeriodFromLocalStorage.product,
      periodName: productPeriodFromLocalStorage.periodName,
      fiscalYearId: productPeriodFromLocalStorage.fiscalYearId,
      loading: false,
      updateProductPeriodFromLocalStorage,
    };
  }

  if (typeof company.businessQuestions.fiscalYearEndMonth !== 'number') {
    throw createError('MissingFiscalYearEndMonthError', 'Fiscal year end month is missing');
  }

  // We have to wait for launchdarkly to be initialized so we can
  // correctly evaluate the enabledAscFeatures + availableRdYears
  if (loadingLaunchDarklyIdentifyCompany) {
    return { product: null, periodName: null, fiscalYearId: null, loading: true, updateProductPeriodFromLocalStorage };
  }

  // MongoDB rules
  if (flags.enableMongoDbSandboxData) {
    const fiscalYear = fiscalYears?.find(
      (fiscalYear) =>
        fiscalYear.enabledDatetime &&
        fiscalYear.productType === ProductType.UsRd &&
        fiscalYear.year === DEFAULT_MONGO_DB_YEAR,
    );

    if (!fiscalYear) {
      throw createError('SandboxCompanyError', `Missing a fiscal year for ${DEFAULT_MONGO_DB_YEAR}`);
    }
    return {
      product: ProductType.UsRd,
      periodName: PeriodName.A,
      fiscalYearId: fiscalYear.id,
      loading: false,
      updateProductPeriodFromLocalStorage,
    };
  }

  // Return the latest fiscal year for RD
  const latestRdFiscalYear = [
    ...fiscalYears.filter((fiscalYear) => fiscalYear.enabledDatetime && fiscalYear.productType === ProductType.UsRd),
  ].sort((a, b) => b.year - a.year)[0];
  // If the latest RD fiscal year exists, use the latest RD fiscal year
  if (latestRdFiscalYear) {
    return {
      product: ProductType.UsRd,
      periodName: PeriodName.A,
      fiscalYearId: latestRdFiscalYear.id,
      loading: false,
      updateProductPeriodFromLocalStorage,
    };
  }

  // If no RD fiscal year exists, return null
  return { product: null, periodName: null, fiscalYearId: null, loading: false, updateProductPeriodFromLocalStorage };
};
